import { IconSearch } from '@maia/icons';
import { Input, Pagination, Table, ToggleButtonGroup } from '@maia/ui';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useEffect } from 'react';
import { useFilter } from '../../../utils/filter/filter';
import { Claims, getClaimsOptions } from './queryOptions';
import { useClaimsTable } from './tableOptions';
import { IClaimEntity } from '@maia/shared';

type ClaimsTableProps = {
  claims: Claims;
};

export function ClaimsTable(props: ClaimsTableProps) {
  const { claims } = props;
  const { state, dispatch: setSearch, mappedState } = useFilter('/claims');
  const { data } = useQuery(getClaimsOptions(mappedState, claims));

  const table = useClaimsTable(data);

  const { sorting } = table.getState();

  useEffect(() => {
    setSearch({ type: 'setSort', payload: sorting });
  }, [setSearch, sorting]);

  const debouncedQuery = debounce(
    (payload: string) => setSearch({ type: 'setQuery', payload }),
    500,
  );

  return (
    <Table
      table={table}
      className="grid-cols-[min-content_auto_auto_auto_min-content_min-content]"
      filterSlot={
        <div className="flex gap-x-24">
          <Input
            icon={<IconSearch />}
            placeholder="Zoeken"
            className="w-full max-w-[550px] mr-auto"
            onChange={(event) => debouncedQuery(event.target.value)}
            defaultValue={state.search}
          />
          <ToggleButtonGroup
            defaultSelectedKey={
              state.filter?.find(([key]) => key === 'status')?.[1]
            }
            items={[
              {
                label: 'Alle',
                value: '',
              },
              {
                label: 'Concept',
                value: 'in_concept' satisfies IClaimEntity['status'],
              },
              {
                label: 'Openstaand',
                value: 'in_progress' satisfies IClaimEntity['status'],
              },
              {
                label: 'Wachtend',
                value: 'in_review' satisfies IClaimEntity['status'],
              },
              {
                label: 'Toegekend',
                value: 'is_closed' satisfies IClaimEntity['status'],
              },
              {
                label: 'Afgewezen',
                value: 'is_cancelled' satisfies IClaimEntity['status'],
              },
            ]}
            onChange={(status) =>
              setSearch({ type: 'setFilter', payload: ['status', status] })
            }
          />
        </div>
      }
      paginationSlot={
        data?.pagination ? (
          <Pagination
            {...data.pagination}
            onLimitChange={(limit) =>
              setSearch({ type: 'setLimit', payload: limit })
            }
            onPageChange={(page) =>
              setSearch({ type: 'paginate', payload: page })
            }
          />
        ) : null
      }
    />
  );
}
